// O OBJETIVO da simulação: o que o usuário quer saber.
// O objetivo deixou de ser um refinamento escondido dentro de "Sugerir melhorias" e passou a ABRIR
// o fluxo: o usuário escolhe onde e o que quer saber, e um único Simular responde.
// A união é DISCRIMINADA de propósito: cada objetivo carrega exatamente o dado que exige, de modo
// que "objetivo sem o seu dado" seja irrepresentável no tipo em vez de virar validação em runtime.
// Arquivo próprio (re-exportado por `sim-sandbox.types.ts` para os consumidores existentes) porque
// aquele já roçava o teto de 200 linhas efetivas — guardrail #1.
/** Janela a avaliar, em horas corridas — o que os objetivos de OEE e de produção pedem. */
export interface ObjectiveWindow {
  hours: number;
}

export type SimulationObjectiveChoice =
  | { kind: "OEE_IMPROVEMENT"; window: ObjectiveWindow }
  | { kind: "TARGET_QUANTITY"; window: ObjectiveWindow; minQuantity?: number }
  | { kind: "TIME_TO_QUANTITY"; quantity: number }
  // Tudo vem da ordem: quantidade (ou saldo), produto, janela e prazo.
  | { kind: "OP_ON_TIME"; productionOrderId: string | null };

/** O objetivo está completo o bastante para simular? (nunca disparar sem o dado.) */
export function objectiveReady(c: SimulationObjectiveChoice | null): boolean {
  if (!c) return false;
  if (c.kind === "OP_ON_TIME") return c.productionOrderId != null;
  if (c.kind === "TIME_TO_QUANTITY") return Number.isFinite(c.quantity) && c.quantity > 0;
  if (!Number.isFinite(c.window.hours) || c.window.hours <= 0) return false;
  // O refinamento é opcional, mas quando preenchido tem de ser válido: um valor negativo passava
  // no gate e só era recusado pelo contrato, como toast genérico sem campo apontado.
  if (c.kind === "TARGET_QUANTITY" && c.minQuantity != null) {
    return Number.isFinite(c.minQuantity) && c.minQuantity > 0;
  }
  return true;
}

/** Qual dado ainda falta, em português — vira a razão exibida quando Simular está bloqueado. */
export function objectiveMissingLabel(c: SimulationObjectiveChoice | null): string | null {
  if (!c) return "Escolha o que você quer saber.";
  if (objectiveReady(c)) return null;
  if (c.kind === "OP_ON_TIME") return "Escolha a Ordem de Produção a avaliar.";
  if (c.kind === "TIME_TO_QUANTITY") return "Informe a quantidade a produzir.";
  if (!Number.isFinite(c.window.hours) || c.window.hours <= 0) return "Informe a janela a avaliar.";
  return "Informe uma quantidade mínima válida.";
}

/**
 * Duas escolhas de objetivo perguntam a MESMA coisa?
 *
 * Comparar por referência marcava a resposta recém-saída como "da pergunta
 * anterior": a escolha restaurada é derivada da última execução, então cada rodada nova produz um
 * objeto novo com o mesmo conteúdo. O aviso existe para o caso real (trocar de pergunta e não
 * simular de novo); disparado a cada rodada, ele treina o usuário a ignorá-lo.
 */
export function sameObjectiveChoice(
  a: SimulationObjectiveChoice | null,
  b: SimulationObjectiveChoice | null
): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === "OP_ON_TIME") {
    return a.productionOrderId === (b as typeof a).productionOrderId;
  }
  if (a.kind === "TIME_TO_QUANTITY") return a.quantity === (b as typeof a).quantity;
  if (a.kind === "TARGET_QUANTITY") {
    const other = b as typeof a;
    return (
      a.window.hours === other.window.hours &&
      (a.minQuantity ?? null) === (other.minQuantity ?? null)
    );
  }
  return a.window.hours === (b as typeof a).window.hours;
}
